import { Page } from '@/lib/components/core/pageItems/page';
import { useGetHandmatigeInvoer, useSendHandmatigeInvoer, useValidate } from '@/lib/queries/manual_input';
import { LeftOutlined, LoadingOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Form, Space, Spin, Steps } from 'antd';
import { StepProps } from 'antd/lib';
import React, { useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, useTransition } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Step1 } from './step_1/page';
import { Step2 } from './step_2/page';
import { Step3 } from './step_3/page';
import { Step4 } from './step_4/page';
import { Step5 } from './step_5/page';
import { Afzender, Relatie } from '@/lib/types/relatie';
import { v4 as uuid } from 'uuid';
import { useMsal } from '@azure/msal-react';
import ResponseModal from './modals/responsemodal';
import { useNavigate } from 'react-router-dom';
import { sanitizeMetingen } from './sanitize-metingen';
import { sanitizeLozingen } from './sanitize-lozingen';
import { getGroundWaterErrors } from './ground-water-rules';
import styles from './page.module.scss';

interface NavigationFeedbackHandle {
  show: () => void;
}

const NavigationFeedback = ({
  busy,
  children,
  ref,
}: React.PropsWithChildren<{
  busy: boolean;
  ref: React.Ref<NavigationFeedbackHandle>;
}>) => {
  const [visible, setVisible] = useState(false);
  useImperativeHandle(ref, () => ({ show: () => setVisible(true) }), []);
  useLayoutEffect(() => setVisible(busy), [busy]);

  return (
    <>
      {visible && (
        <Spin
          fullscreen
          size="large"
          rootClassName={styles.stepLoading}
          indicator={<LoadingOutlined spin style={{ fontSize: 64 }} />}
          aria-label="Bezig met laden"
        />
      )}
      <div aria-busy={visible} inert={visible}>
        {children}
      </div>
    </>
  );
};

export const ManualInputPage: React.FunctionComponent = () => {
  const { t } = useTranslation('input');
  const [form] = Form.useForm<Relatie>();
  const navigate = useNavigate();

  const registratieJaar = Form.useWatch('registratiejaar', form);
  const uonummer = Form.useWatch('uoNummer', form);

  const [currentStep, setCurrentStep] = useState(0);
  const [isChangingStep, startStepTransition] = useTransition();
  const navigationFeedback = useRef<NavigationFeedbackHandle>(null);
  const navigationStarted = useRef(false);
  const [responseContent, setResponseContent] = useState<string | null>(null);
  const [modalErrorContent, setModalErrorContent] = useState<string | null>(null);

  const [formAfzender, setFormAfzender] = useState<Afzender>({} as Afzender);

  const { instance: msalInstance } = useMsal();
  const { mutate: mutateHandmatigeInvoer, isPending: isSaving } = useSendHandmatigeInvoer();
  const { mutate: validate, isPending: isValidating } = useValidate();
  const isBusy = isChangingStep || isValidating || isSaving;
  const {
    data: handmatigeInvoerData,
    isLoading: handmatigeInvoerIsLoading,
    isError: handmatigeInvoerIsError,
  } = useGetHandmatigeInvoer(registratieJaar, uonummer);

  useEffect(() => {
    if (handmatigeInvoerData) {
      form.setFieldsValue(handmatigeInvoerData);
    }
  }, [handmatigeInvoerData, form]);

  const [items, setItems] = useState<StepProps[]>([
    {
      status: 'process',
      title: 'Introductie',
      disabled: false,
    },
    {
      status: 'wait',
      title: 'Bedrijfsgegevens',
      disabled: true,
    },
    {
      status: 'wait',
      title: 'Milieugegevens',
      disabled: true,
    },
    {
      status: 'wait',
      title: 'Teeltplan',
      disabled: true,
    },
    {
      status: 'wait',
      title: 'Versturen',
      disabled: true,
    },
  ]);

  const goToStep = (step: number) => {
    if (isBusy || step === currentStep || step < 0 || step >= items.length || items[step]?.disabled) {
      return;
    }

    if (currentStep === 1 && step > currentStep) {
      validateCompanyInfo();
      return;
    }

    startNavigation(() => setCurrentStep(step));
  };

  const updateStep = (page: number) => {
    startStepTransition(() => {
      setItems(current => {
        const updated = current.map(item => ({ ...item }));
        updated[page].status = 'finish';
        updated[page].disabled = page === 0;

        if (updated[page + 1].status !== 'finish') {
          updated[page + 1].status = 'process';
          updated[page + 1].disabled = false;
        }
        return updated;
      });
      setCurrentStep(page + 1);
    });
  };

  const startNavigation = (action: () => void | Promise<void>) => {
    if (isBusy || navigationStarted.current) return;
    navigationStarted.current = true;
    flushSync(() => navigationFeedback.current?.show());
    requestAnimationFrame(() =>
      setTimeout(
        () =>
          startStepTransition(async () => {
            try {
              await action();
            } catch (error) {
              if (!(error && typeof error === 'object' && 'errorFields' in error)) {
                setModalErrorContent('Er is een onverwachte fout opgetreden. Neem contact op met de servicedesk.');
              }
            } finally {
              navigationStarted.current = false;
            }
          }),
        0
      )
    );
  };

  const runStepAction = (action: () => void) => {
    startNavigation(async () => {
      await form.validateFields();
      action();
    });
  };

  function createUpdatedAfzender(currentAfzender: Afzender, currentValues: Relatie, milieuValidatie: boolean, teeltplanRegelsValidatie: boolean): Afzender {
    return {
      ...currentAfzender,
      isMilieugegevensValidatie: milieuValidatie,
      isTeeltplanRegelsValidatie: teeltplanRegelsValidatie,
      relatie: [
        {
          ...currentAfzender.relatie[0],
          ...currentValues,
        },
      ],
    };
  }

  function updateAfzender(currentValues: Relatie, milieuValidatie: boolean, teeltplanRegelsValidatie: boolean) {
    const afzender = createUpdatedAfzender(formAfzender, currentValues, milieuValidatie, teeltplanRegelsValidatie);
    setFormAfzender(afzender);
    if (currentStep >= 1 && currentStep <= 3) {
      validateAfzender(afzender);
    }
  }

  function validateAfzender(afzender: Afzender) {
    const validationAfzender = {
      ...afzender,
      relatie: afzender.relatie.map(relatie => ({ ...relatie, verzendBevestiging: undefined })),
    };
    validate(validationAfzender, {
      onSuccess: (response: any) => {
        //Check if response is OK result
        const isSuccess = response?.relaties?.some(relatie => relatie?.foutBerichten?.some(foutBericht => foutBericht?.code === 0));

        //Check if there are warnings
        const containsWarnings = response?.relaties?.some(relatie => relatie?.foutBerichten?.some(foutBericht => foutBericht?.code != 0));

        if (isSuccess && currentStep !== 4) {
          if (containsWarnings) {
            setResponseContent(JSON.stringify(response, null, 2));
          } else {
            updateStep(currentStep);
          }
        } else {
          setResponseContent(JSON.stringify(response, null, 2));
        }
      },
      onError: (response: any) => {
        setResponseContent(JSON.stringify(response, null, 2));
        setModalErrorContent('Er is een onverwachte fout opgetreden. Neem contact op met de servicedesk.');
      },
    });
  }

  const onSubmitStep1 = async () => {
    runStepAction(() => {
      const account = msalInstance.getActiveAccount();
      const username = account?.username || 'unknown';
      const currentValues = form.getFieldsValue();
      setFormAfzender(prev => {
        return {
          ...prev,
          berichtVersie: '03.00',
          berichtID: uuid(),
          timestamp: new Date().toISOString(),
          isDeelRapportage: currentValues['isDeelRapportage'] ? 1 : 2,
          isUpdate: true,
          loginNaam: username.toString(),
          relatie: [
            {
              registratiejaar: currentValues['registratiejaar'],
            },
          ] as Relatie[],
        };
      });
      updateStep(currentStep);
    });
  };

  const onSubmitStep2 = async () => {
    validateCompanyInfo();
  };

  function validateCompanyInfo() {
    runStepAction(() => {
      const currentRelatie = form.getFieldsValue(true);
      updateAfzender({ ...currentRelatie, lozingsVorm: currentRelatie.lozingsVorm ?? [] }, true, true);
    });
  }

  const onSubmitStep3 = async () => {
    runStepAction(() => {
      const sanitizedRelatie = sanitize(form.getFieldsValue(true));
      updateAfzender(sanitizedRelatie, true, false);
    });
  };

  const onSubmitStep4 = async () => {
    runStepAction(() => {
      updateAfzender(sanitize(form.getFieldsValue(true)), false, true);
    });
  };

  const onSubmitStep5 = async () => {
    runStepAction(() => {
      const groundWaterErrors = getGroundWaterErrors(form.getFieldsValue(true));
      if (groundWaterErrors.length > 0) {
        form.setFields(groundWaterErrors);
        setModalErrorContent([...new Set(groundWaterErrors.flatMap(error => error.errors))].join('\n'));
        return;
      }
      const currentRelatie = sanitize(form.getFieldsValue(true));
      const afzender = createUpdatedAfzender(formAfzender, currentRelatie, false, false);
      setFormAfzender(afzender);
      submitAfzender(afzender);
    });
  };

  function sanitize(relatie: Relatie): Relatie {
    const geldigeTeeltwijzen = relatie.teeltwijze === 2 ? [0, 1] : [relatie.teeltwijze];
    const updatedRelatie = {
      ...relatie,
      ...sanitizeLozingsVorm(relatie),
      metingen: relatie.metingen
        ?.filter(meting => geldigeTeeltwijzen.includes(meting?.betrekkingTotTeeltwijze ?? -1))
        .filter((meting, index, alleMetingen) => alleMetingen.findIndex(item => item.betrekkingTotTeeltwijze === meting.betrekkingTotTeeltwijze) === index),
    };

    return {
      ...updatedRelatie,
      ...sanitizeMetingen(updatedRelatie),
      ...sanitizeLozingen(updatedRelatie),
    };
  }

  function sanitizeLozingsVorm(relatie: Relatie) {
    const geldigeTeeltwijzen = relatie.teeltwijze === 2 ? [0, 1] : [relatie.teeltwijze];
    let lozingsVormen = relatie.lozingsVorm?.filter(l => l !== undefined && l !== null && geldigeTeeltwijzen.includes(l.betrekkingTotTeeltwijze));
    lozingsVormen = lozingsVormen?.filter(
      (lozingsVorm, index, alleLozingsVormen) =>
        alleLozingsVormen.findIndex(item => item.betrekkingTotTeeltwijze === lozingsVorm.betrekkingTotTeeltwijze) === index
    );

    if (lozingsVormen === null || lozingsVormen === undefined) {
      return;
    }

    return {
      lozingsVorm: lozingsVormen,
    };
  }

  const onModalOk = () => {
    setResponseContent(null);
    setModalErrorContent(null);

    let responseData = JSON.parse(responseContent || '');

    if (currentStep === 4) {
      if (responseData?.relaties?.some(relatie => relatie?.foutBerichten?.some(foutBericht => foutBericht?.code === 0 || foutBericht?.code === 4))) {
        navigate('/');
        return;
      }
    } else if (responseData?.relaties?.some(relatie => relatie?.foutBerichten?.some(foutBericht => foutBericht?.code === 0))) {
      updateStep(currentStep);
    }
  };

  const submitAfzender = (afzender: Afzender) => {
    try {
      mutateHandmatigeInvoer(afzender, {
        onSuccess: response => {
          setResponseContent(JSON.stringify(response, null, 2));
        },
        onError: response => {
          setResponseContent(JSON.stringify(response, null, 2));
          setModalErrorContent('Er is een onverwachte fout opgetreden. Neem contact op met de servicedesk.');
        },
      });
    } catch (e) {
      setModalErrorContent('Er is een onverwachte fout opgetreden. Neem contact op met de servicedesk.');
    }
  };

  const onCompleteFormSubmit = async () => {
    submitAfzender(formAfzender);
  };

  const steps = [
    <Step1 onSubmit={onSubmitStep1} isLoading={handmatigeInvoerIsLoading} isError={handmatigeInvoerIsError} />,
    <Step2 onSubmit={onSubmitStep2} />,
    <Step3 onSubmit={onSubmitStep3} isValidating={isValidating} />,
    <Step4 onSubmit={onSubmitStep4} isValidating={isValidating} />,
    <Step5 onSubmit={onSubmitStep5} isSaving={isSaving} />,
  ];

  const previousStep = currentStep - 1;
  const nextStep = currentStep + 1;
  const canGoPrevious = previousStep >= 0 && !items[previousStep]?.disabled;
  const canGoNext = nextStep < items.length && !items[nextStep]?.disabled;

  return (
    <NavigationFeedback ref={navigationFeedback} busy={isBusy}>
      <Space style={{ width: '100%' }} direction="vertical">
        <div style={{ alignItems: 'center', display: 'flex', gap: '12px', margin: '0 auto', maxWidth: '1080px', width: '100%' }}>
          <Button
            aria-label="Ga naar de vorige stap"
            disabled={!canGoPrevious}
            icon={<LeftOutlined />}
            onClick={() => goToStep(previousStep)}
            shape="circle"
            type="primary"
          />
          <Steps
            style={{ flex: 1, marginBottom: '20px' }}
            type="navigation"
            size="small"
            current={currentStep}
            onChange={goToStep}
            className="site-navigation-steps"
            items={items}
          />
          <Button
            aria-label="Ga naar de volgende stap"
            disabled={!canGoNext}
            icon={<RightOutlined />}
            onClick={() => goToStep(nextStep)}
            shape="circle"
            type="primary"
          />
        </div>
        <Page title={t(`form.title.${currentStep}`)}>
          <Form onFinish={onCompleteFormSubmit} form={form}>
            {steps[currentStep]}
          </Form>
        </Page>
        <ResponseModal responseContent={responseContent} error={modalErrorContent} onOk={onModalOk} />
      </Space>
    </NavigationFeedback>
  );
};
